/*
 * The push subscription lifecycle of this browser (docs/PWA.md "Push lifecycle"), with the browser and the API
 * injected so it is unit tested without either (test/pwa-subscription.test.ts):
 *
 *   enable    on a user gesture only: ask permission (when not decided yet), subscribe with the server's VAPID key,
 *             register the subscription (`POST /me/push-subscriptions`, an upsert keyed by endpoint), remember the
 *             key id it was made under
 *   sync      on app start while signed in, never prompting: an existing subscription made under another key (the
 *             server rotated its VAPID key) is replaced; a current one is registered again (idempotent), so the
 *             server's record follows this browser and the signed-in account
 *   disable   unregister on the server, then unsubscribe the browser
 *
 * `pushsubscriptionchange` (the push service rotated the endpoint) is handled in the worker (src/pwa/sw.ts).
 */
import { sameServerKey } from './keys'

export const KEY_ID_STORE = 'algoboo.push.keyId'

export interface ServerKey {
  publicKey: string
  keyId: string | null
}

export interface SubscriptionInput {
  endpoint: string
  keys: { p256dh: string; auth: string }
  expirationTime: number | null
}

export interface BrowserSubscription {
  endpoint: string
  options?: { applicationServerKey?: ArrayBuffer | null }
  toJSON(): { endpoint?: string; keys?: Record<string, string | undefined>; expirationTime?: number | null }
  unsubscribe(): Promise<boolean>
}

export interface PushBrowser {
  getSubscription(): Promise<BrowserSubscription | null>
  subscribe(publicKey: string): Promise<BrowserSubscription>
  permission(): NotificationPermission
  requestPermission(): Promise<NotificationPermission>
  store: { get(k: string): string | null; set(k: string, v: string): void; remove(k: string): void }
}

export interface PushServer {
  register(sub: SubscriptionInput): Promise<void>
  unregister(endpoint: string): Promise<void>
}

export class PushSetupError extends Error {
  constructor(
    readonly code: 'denied' | 'incomplete',
    message: string,
  ) {
    super(message)
  }
}

/** Made under another key than the server's current one? */
export function isStale(sub: BrowserSubscription, key: ServerKey, storedKeyId: string | null): boolean {
  const same = sameServerKey(sub.options?.applicationServerKey, key.publicKey)
  if (same !== null) return !same
  return !!key.keyId && !!storedKeyId && storedKeyId !== key.keyId
}

function wire(sub: BrowserSubscription): SubscriptionInput {
  const j = sub.toJSON()
  const p256dh = j.keys?.['p256dh']
  const auth = j.keys?.['auth']
  if (!j.endpoint || !p256dh || !auth) throw new PushSetupError('incomplete', 'The browser returned an incomplete push subscription.')
  return { endpoint: j.endpoint, keys: { p256dh, auth }, expirationTime: j.expirationTime ?? null }
}

async function drop(b: PushBrowser, s: PushServer, sub: BrowserSubscription): Promise<void> {
  await s.unregister(sub.endpoint).catch(() => undefined)
  await sub.unsubscribe().catch(() => false)
}

/** Subscribe (or keep the current subscription), replace a stale one, register it; returns the endpoint. */
async function ensure(b: PushBrowser, s: PushServer, key: ServerKey): Promise<{ endpoint: string; rotated: boolean }> {
  let sub = await b.getSubscription()
  let rotated = false
  if (sub && isStale(sub, key, b.store.get(KEY_ID_STORE))) {
    await drop(b, s, sub)
    sub = null
    rotated = true
  }
  sub ??= await b.subscribe(key.publicKey)
  await s.register(wire(sub))
  if (key.keyId) b.store.set(KEY_ID_STORE, key.keyId)
  return { endpoint: sub.endpoint, rotated }
}

/** Turn push on (call from a click): permission, subscription, server record. Returns the endpoint. */
export async function enablePushWith(b: PushBrowser, s: PushServer, key: ServerKey): Promise<string> {
  let permission = b.permission()
  if (permission === 'default') permission = await b.requestPermission()
  if (permission !== 'granted') throw new PushSetupError('denied', 'Notifications are blocked for algo.boo in this browser. Allow them in the site settings, then try again.')
  return (await ensure(b, s, key)).endpoint
}

/** App start: keep an existing subscription current. Never prompts and never subscribes a browser that is off. */
export async function syncPushWith(b: PushBrowser, s: PushServer, key: ServerKey): Promise<'off' | 'ok' | 'rotated'> {
  if (b.permission() !== 'granted') return 'off'
  if (!(await b.getSubscription())) return 'off'
  return (await ensure(b, s, key)).rotated ? 'rotated' : 'ok'
}

/** Turn push off on this browser. */
export async function disablePushWith(b: PushBrowser, s: PushServer): Promise<void> {
  const sub = await b.getSubscription()
  b.store.remove(KEY_ID_STORE)
  if (sub) await drop(b, s, sub)
}

export type PushCapability = 'ok' | 'ios-install' | 'unsupported'

/**
 * Can this browser get web push? iOS and iPadOS deliver web push only to a home-screen app (16.4+): in a Safari tab
 * the answer is `ios-install` (explain Share → Add to Home Screen) rather than a hidden switch.
 */
export function pushCapability(e: { ios: boolean; standalone: boolean; hasPushManager: boolean; hasServiceWorker: boolean; hasNotification: boolean }): PushCapability {
  if (e.hasPushManager && e.hasServiceWorker && e.hasNotification) return 'ok'
  if (e.ios && !e.standalone && e.hasServiceWorker) return 'ios-install'
  return 'unsupported'
}
